/**
 * SystemConfigPage — 系统配置（地区候选 / 语言候选）
 *
 * 承接 ADM-SYS-S001（地区候选）/ ADM-SYS-S002（语言候选，PR #63）。
 * 地区与语言均为系统级元数据：admin 仅可 toggle `enabled` 启停；主键、display_name、app_selectable
 * 由发版 seed / DB migration 维护，不在 admin 编辑入口暴露。
 * 语言表中 EN 行 lockEnabled=true（DB CHECK 强制 enabled=true，作为 fallback 边界），不可停用。
 * 取数走冻结 hook useSysConfig()（语言 + 区域）；toggle 已接真：PATCH
 * /system-config/(languages|regions)/:code（useMutation），成功按服务端真值 toast +
 * invalidate ['sys-config'] 刷新，失败报错并回滚 Switch 视觉态（不假成功）。
 */

import type { PopupAccessLevelResponse, PopupAccessLevelScenario } from '@novel-admin/shared';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adaptSystemLanguage, adaptSystemRegion } from '~/data/adapters/sysconfig-adapter';
import { useApps, useSysConfig } from '~/data/queries';
import type { SystemLanguage, SystemRegion } from '~/data/queries';
import { systemConfigApi } from '~/hooks/use-system-config';
import { Icons } from '~/lib/icons';
import { scenarioLabel } from '~/pages/ops/popup-scenarios';
import { useAuthStore } from '~/stores/auth-store';
import { ConfirmDialog } from '~/ui/Dialog';
import { Switch } from '~/ui/Switch';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';

/** sys-config 查询缓存形状（useSysConfig 的 queryFn 返回值） */
type SysConfigCache = { languages: SystemLanguage[]; regions: SystemRegion[] };

/**
 * 把 PATCH 返回的单行 DTO 合并进 ['sys-config'] 查询缓存（先于 invalidate）。
 * Why：onSuccess 只更本地 rows 不够——refetch 在途/失败时共享缓存仍是旧值，切 tab/
 * 重挂 / 其他 useSysConfig 消费方会从 stale 缓存初始化、显示旧 enabled（codex #53 CLI）。
 */
function patchSysConfigCache(
  queryClient: QueryClient,
  patch: (cache: SysConfigCache) => SysConfigCache,
) {
  queryClient.setQueryData<SysConfigCache>(['sys-config'], (old) => (old ? patch(old) : old));
}

/** 写角色闭集（root/admin/operator）；viewer 只读——前端防线，后端 requireRole 才是权威 */
function useCanWriteSystemConfig(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin' || role === 'operator';
}

/**
 * 运营弹窗 access level 写角色闭集（root/admin）——**比语言 / 地区窄一档**。
 * A5 §11.2：本值影响全部 App，且一次写入会递增全局纪元、令进行中的 Stripe Checkout 走收敛
 * 路径，故 operator 只读。沿用 useCanWriteSystemConfig 会让 operator 点完吃 403。
 */
function useCanWritePopupAccessLevels(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'root' || role === 'admin';
}

/** 段切换 tab 配置项（id / 文案 / 图标 / 需求号）*/
interface SubTab {
  id: 'regions' | 'languages' | 'popup-access-levels';
  label: string;
  icon: keyof typeof Icons;
  short: string;
}

export default function SystemConfigPage() {
  // 子档可由 URL `?sub=` 指定：运营弹窗页的「弹窗权限档」链接靠它直达，否则会落在默认的地区表上。
  // 取值只认闭集，未知值回落 regions（防 URL 乱传渲染出空白页）。
  const [searchParams, setSearchParams] = useSearchParams();
  const subParam = searchParams.get('sub');
  const initialSub =
    subParam === 'languages' || subParam === 'popup-access-levels' ? subParam : 'regions';
  const [sub, setSub] = useState<'regions' | 'languages' | 'popup-access-levels'>(initialSub);
  // URL 变化（如从别处再次点同一链接）时跟随切换
  useEffect(() => {
    if (subParam === 'languages' || subParam === 'popup-access-levels' || subParam === 'regions') {
      setSub(subParam);
    }
  }, [subParam]);
  // 本页不在父级做整页 isLoading 骨架门控：RegionsTable / LanguagesTable 各自消费 ['sys-config']，
  // 父级再加一个 observer 会与 staleTime:0 + 子表挂载在「初次取数失败」路径下相互触发无限重取
  // （死循环回归，见 docs/plans/2026-06-17-loading-skeleton-system.md NOT-in-scope）。
  // 加载反馈走 AppShell 顶部 TopBar（useIsFetching）；整页骨架对本页不适用。
  const subTabs: SubTab[] = [
    { id: 'regions', label: '地区候选', icon: 'IconGlobe', short: 'ADM-SYS-S001' },
    { id: 'languages', label: '语言候选', icon: 'IconLanguages', short: 'ADM-SYS-S002' },
    {
      id: 'popup-access-levels',
      label: '弹窗权限档',
      icon: 'IconFlame',
      short: 'ADM-SYS-S003',
    },
  ];
  return (
    // page-enter：数据到达后淡入，复刻原型加载完成的过渡
    <div className="vstack page-enter" style={{ gap: 14 }}>
      <div
        className="card-flat"
        style={{ padding: '10px 12px', background: 'var(--info-soft)', borderColor: 'transparent' }}
      >
        <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
          <Icons.IconInfo size={13} style={{ color: 'var(--info)', marginTop: 2, flexShrink: 0 }} />
          <div style={{ fontSize: 11.5, color: 'var(--fg)', lineHeight: 1.55 }}>
            <span style={{ fontWeight: 500 }}>ADM-SYS · 系统配置域</span> · 2026-05-28 PR #63 落地。
            <span style={{ color: 'var(--fg-muted)' }}>
              {' '}
              地区与语言均为系统级元数据，admin 仅可调整 <span className="mono">enabled</span>{' '}
              启停；主键、显示名、<span className="mono">app_selectable</span> 由发版 seed
              维护，不在 admin 编辑。
            </span>
          </div>
        </div>
      </div>

      <div className="hstack" style={{ gap: 6 }}>
        <div className="seg">
          {subTabs.map((t) => {
            const Icon = Icons[t.icon];
            return (
              <button
                key={t.id}
                className={sub === t.id ? 'on' : ''}
                onClick={() => {
                  setSub(t.id);
                  // 同步进 URL：刷新 / 分享链接能回到同一子档
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('sub', t.id);
                    return next;
                  });
                }}
              >
                <Icon size={11} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {sub === 'regions' && <RegionsTable />}
      {sub === 'languages' && <LanguagesTable />}
      {sub === 'popup-access-levels' && <PopupAccessLevelsTable />}
    </div>
  );
}

/** 地区候选表 — 仅 enabled 可改；display_name / region_code 只读 */
function RegionsTable() {
  const { data, isSuccess } = useSysConfig();
  const queryClient = useQueryClient();
  const canWrite = useCanWriteSystemConfig();
  const [rows, setRows] = useState<SystemRegion[]>(() => data.regions.map((r) => ({ ...r })));
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  // 失败回滚计数：共享 Switch 点击会先自翻视觉态，PATCH 失败时行数据未变、
  // 仅靠 prop 同步无法复位 —— bump nonce 触发 Switch 重挂回服务端真值
  const [switchResetNonce, setSwitchResetNonce] = useState(0);
  // rows 镜像服务端真值，仅在查询**成功落地**后同步（含成功返回空集 → 照实清空）。
  // 用 isSuccess 而非 !isLoading：error 兜底也 isLoading=false，但其 data 是稳定空 fallback、
  // 不该被当成「服务端真值空」覆盖最后已知行（adversarial #53 r2：!isLoading guard + 内联
  // fallback 新引用会在初次取数失败时 setRows 死循环 + 误清行）。失败时保留最后已知行。
  useEffect(() => {
    if (!isSuccess) return;
    setRows(data.regions.map((r) => ({ ...r })));
  }, [data.regions, isSuccess]);
  const toggleMutation = useMutation({
    mutationFn: ({ code, enabled }: { code: string; enabled: boolean }) =>
      systemConfigApi.updateRegionEnabled(code, enabled),
    // async + await invalidate：isPending 覆盖到 refetch 落地，关掉「响应已回、列表未刷、
    // Switch 复活」的窗口（adversarial #53 r1-1：窗口内二次点击会按 stale 值反向 PATCH 误撤销）
    onSuccess: async (dto) => {
      const adapted = adaptSystemRegion(dto);
      // 先用 mutation DTO 同步对账本行（不等 GET），chip/开关立即反映服务端真值
      setRows((prev) =>
        prev.some((r) => r.region_code === dto.region_code)
          ? prev.map((r) => (r.region_code === dto.region_code ? adapted : r))
          : prev,
      );
      // 同步写入共享缓存（先于 invalidate）：避免切 tab/重挂从 stale 缓存初始化旧 enabled
      patchSysConfigCache(queryClient, (cache) => ({
        ...cache,
        regions: cache.regions.map((r) => (r.region_code === dto.region_code ? adapted : r)),
      }));
      // toast 用服务端返回真值（修原型 P-47：读 setState 前旧值的 stale 文案）
      toast(`${dto.region_code} · 已${dto.enabled ? '启用' : '停用'}`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      await queryClient.invalidateQueries({ queryKey: ['sys-config'] });
    },
    // 失败 ≠ 一定没写入（响应丢失/超时/解码失败都走这里）：回滚视觉态后必须 refetch
    // 服务端真值对账，不能盲信「旧状态」（adversarial #53 r1-2）
    onError: async (err, vars) => {
      setSwitchResetNonce((n) => n + 1);
      toast(`${vars.code} · ${err instanceof Error ? err.message : '操作失败'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
      await queryClient.invalidateQueries({ queryKey: ['sys-config'] });
    },
  });
  const toggle = (code: string, enabled: boolean) => toggleMutation.mutate({ code, enabled });
  const filtered = rows.filter((r) => {
    if (
      q &&
      !(
        r.region_code.toLowerCase().includes(q.toLowerCase()) ||
        (r.display_name_i18n.zh || '').includes(q) ||
        (r.display_name_i18n.en || '').toLowerCase().includes(q.toLowerCase())
      )
    )
      return false;
    if (statusFilter === 'enabled' && !r.enabled) return false;
    if (statusFilter === 'disabled' && r.enabled) return false;
    return true;
  });
  const enabledCount = rows.filter((r) => r.enabled).length;
  return (
    <>
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div>
          <h2 style={{ font: 'inherit', fontSize: 16, fontWeight: 600, margin: 0 }}>地区候选</h2>
          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 3 }}>
            <span className="mono">system_regions</span> · {rows.length} 项 ·{' '}
            <span style={{ color: 'var(--pos)' }}>{enabledCount}</span> 启用 · 派生为
            App「地区」字段下拉
          </div>
        </div>
        <div className="hstack" style={{ gap: 6 }}>
          <div className="seg">
            <button
              className={statusFilter === 'all' ? 'on' : ''}
              onClick={() => setStatusFilter('all')}
            >
              全部
            </button>
            <button
              className={statusFilter === 'enabled' ? 'on' : ''}
              onClick={() => setStatusFilter('enabled')}
            >
              启用
            </button>
            <button
              className={statusFilter === 'disabled' ? 'on' : ''}
              onClick={() => setStatusFilter('disabled')}
            >
              停用
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <Icons.IconSearch
              size={11}
              style={{ position: 'absolute', left: 8, top: 7, color: 'var(--fg-faint)' }}
            />
            <input
              className="input"
              placeholder="代码 / 名称"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 200, height: 26, paddingLeft: 26, fontSize: 11.5 }}
            />
          </div>
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <Table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 70 }}>代码</th>
              <th>中文名</th>
              <th>英文名</th>
              <th>本地名</th>
              <th style={{ width: 120 }}>状态</th>
              <th>最近更新</th>
              <th style={{ width: 80 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.region_code}>
                <td className="mono" style={{ fontWeight: 600 }}>
                  {r.region_code}
                </td>
                <td>{r.display_name_i18n.zh}</td>
                <td style={{ color: 'var(--fg-muted)' }}>{r.display_name_i18n.en}</td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  {Object.entries(r.display_name_i18n)
                    .filter(([k]) => k !== 'zh' && k !== 'en')
                    .map(([, v]) => v)
                    .join(' · ') || '—'}
                </td>
                <td>
                  {r.enabled ? (
                    <span className="chip chip-pos" style={{ fontSize: 10 }}>
                      ● 启用
                    </span>
                  ) : (
                    <span className="chip" style={{ fontSize: 10, color: 'var(--fg-muted)' }}>
                      ○ 停用
                    </span>
                  )}
                </td>
                <td>
                  <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{r.updatedAt}</div>
                </td>
                <td>
                  <Switch
                    key={`${r.region_code}:${switchResetNonce}`}
                    on={r.enabled}
                    disabled={!canWrite || toggleMutation.isPending}
                    onChange={(next) => toggle(r.region_code, next)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {filtered.length === 0 && (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 12 }}>
            无匹配地区
          </div>
        )}
      </div>

      <ReadOnlyNote
        kind="region"
        items={[
          { code: 'system_regions.region_code', desc: 'ISO 3166-1 alpha-2 · 主键 · 不可改' },
          {
            code: 'system_regions.display_name_i18n',
            desc: '多语言名 · 由 seed 维护，admin 不编辑',
          },
        ]}
      />
    </>
  );
}

/** 语言候选表 — enabled 可改（EN 行 lockEnabled 锁定）；app_selectable / display_name 只读 */
function LanguagesTable() {
  const { data, isSuccess } = useSysConfig();
  const {
    data: apps,
    isLoading: appsLoading,
    isError: appsError,
    refetch: refetchApps,
  } = useApps();
  const [languageToDisable, setLanguageToDisable] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const canWrite = useCanWriteSystemConfig();
  const [rows, setRows] = useState<SystemLanguage[]>(() => data.languages.map((r) => ({ ...r })));
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled'>('all');
  // 同 RegionsTable：失败时 bump nonce 重挂 Switch，回滚其自翻的视觉态
  const [switchResetNonce, setSwitchResetNonce] = useState(0);
  // rows 仅在查询成功落地后镜像（含成功空集照实清空）；失败保留最后已知行
  // （同 RegionsTable，adversarial #53 r1-3 / r2 — isSuccess guard 防 error-fallback 死循环）
  useEffect(() => {
    if (!isSuccess) return;
    setRows(data.languages.map((r) => ({ ...r })));
  }, [data.languages, isSuccess]);
  const toggleMutation = useMutation({
    mutationFn: ({ code, enabled }: { code: string; enabled: boolean }) =>
      systemConfigApi.updateLanguageEnabled(code, enabled),
    // async + 先 DTO 对账本行再 await invalidate（同 RegionsTable，adversarial #53 r1-1）
    onSuccess: async (dto) => {
      const adapted = adaptSystemLanguage(dto);
      setRows((prev) =>
        prev.some((r) => r.language_code === dto.language_code)
          ? prev.map((r) => (r.language_code === dto.language_code ? adapted : r))
          : prev,
      );
      // 同步写共享缓存（先于 invalidate），防切 tab/重挂读 stale enabled（同 RegionsTable）
      patchSysConfigCache(queryClient, (cache) => ({
        ...cache,
        languages: cache.languages.map((r) =>
          r.language_code === dto.language_code ? adapted : r,
        ),
      }));
      toast(`${dto.language_code.toUpperCase()} · 已${dto.enabled ? '启用' : '停用'}`, {
        tone: 'pos',
        icon: 'IconCheck',
      });
      await queryClient.invalidateQueries({ queryKey: ['sys-config'] });
    },
    // 失败后 refetch 对账服务端真值（写可能已提交，同 RegionsTable，adversarial #53 r1-2）
    onError: async (err, vars) => {
      setSwitchResetNonce((n) => n + 1);
      toast(`${vars.code.toUpperCase()} · ${err instanceof Error ? err.message : '操作失败'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
      await queryClient.invalidateQueries({ queryKey: ['sys-config'] });
    },
  });
  const toggle = (code: string, enabled: boolean) => {
    const row = rows.find((r) => r.language_code === code);
    if (!row || row.lockEnabled) return;
    if (!enabled) {
      if (appsLoading || appsError) {
        setSwitchResetNonce((n) => n + 1);
        return;
      }
      setLanguageToDisable(code);
      return;
    }
    toggleMutation.mutate({ code, enabled });
  };
  const closeLanguageWarning = () => {
    setLanguageToDisable(null);
    setSwitchResetNonce((n) => n + 1);
  };
  const filtered = rows.filter((r) => {
    if (
      q &&
      !(
        r.language_code.includes(q.toLowerCase()) ||
        (r.display_name_i18n.zh || '').includes(q) ||
        (r.display_name_i18n.en || '').toLowerCase().includes(q.toLowerCase())
      )
    )
      return false;
    if (statusFilter === 'enabled' && !r.enabled) return false;
    if (statusFilter === 'disabled' && r.enabled) return false;
    return true;
  });
  const enabledCount = rows.filter((r) => r.enabled).length;
  const appSelectableEnabledCount = rows.filter((r) => r.enabled && r.app_selectable).length;
  return (
    <>
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div>
          <h2 style={{ font: 'inherit', fontSize: 16, fontWeight: 600, margin: 0 }}>语言候选</h2>
          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 3 }}>
            <span className="mono">system_languages</span> · {rows.length} 项 ·{' '}
            <span style={{ color: 'var(--pos)' }}>{enabledCount}</span> 启用 · 其中{' '}
            <span className="mono">{appSelectableEnabledCount}</span> 可作 App 运行语言
          </div>
        </div>
        <div className="hstack" style={{ gap: 6 }}>
          <div className="seg">
            <button
              className={statusFilter === 'all' ? 'on' : ''}
              onClick={() => setStatusFilter('all')}
            >
              全部
            </button>
            <button
              className={statusFilter === 'enabled' ? 'on' : ''}
              onClick={() => setStatusFilter('enabled')}
            >
              启用
            </button>
            <button
              className={statusFilter === 'disabled' ? 'on' : ''}
              onClick={() => setStatusFilter('disabled')}
            >
              停用
            </button>
          </div>
          <div style={{ position: 'relative' }}>
            <Icons.IconSearch
              size={11}
              style={{ position: 'absolute', left: 8, top: 7, color: 'var(--fg-faint)' }}
            />
            <input
              className="input"
              placeholder="代码 / 名称"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ width: 200, height: 26, paddingLeft: 26, fontSize: 11.5 }}
            />
          </div>
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <Table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 70 }}>代码</th>
              <th>中文名</th>
              <th>英文名</th>
              <th style={{ width: 130 }}>
                可作 App 运行语言
                <span
                  title="是否可作为 C 端 App 运行语言被选 · 只读 · 由 seed 维护"
                  style={{ marginLeft: 4, color: 'var(--fg-faint)', cursor: 'help' }}
                >
                  <Icons.IconInfo size={10} style={{ verticalAlign: 'middle' }} />
                </span>
              </th>
              <th style={{ width: 150 }}>
                默认时区
                <span
                  title="IANA 时区 · 书籍定时发布按该语言默认时区计算 · 只读 · 由 seed 维护"
                  style={{ marginLeft: 4, color: 'var(--fg-faint)', cursor: 'help' }}
                >
                  <Icons.IconInfo size={10} style={{ verticalAlign: 'middle' }} />
                </span>
              </th>
              <th style={{ width: 120 }}>状态</th>
              <th>最近更新</th>
              <th style={{ width: 80 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.language_code}>
                <td>
                  <span className="mono" style={{ fontWeight: 600 }}>
                    {r.language_code.toUpperCase()}
                  </span>
                  {r.lockEnabled && (
                    <Icons.IconLock
                      size={10}
                      style={{ color: 'var(--fg-muted)', marginLeft: 5, verticalAlign: 'middle' }}
                    />
                  )}
                </td>
                <td>{r.display_name_i18n.zh}</td>
                <td style={{ color: 'var(--fg-muted)' }}>{r.display_name_i18n.en}</td>
                <td>
                  {r.app_selectable ? (
                    <span className="chip chip-info" style={{ fontSize: 10 }}>
                      <Icons.IconCheck size={9} />
                      可选
                    </span>
                  ) : (
                    <span className="chip" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
                      —
                    </span>
                  )}
                </td>
                <td>
                  <span
                    className="mono"
                    style={{
                      fontSize: 11,
                      color: 'var(--fg-muted)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <Icons.IconLock size={9} style={{ color: 'var(--fg-faint)', flexShrink: 0 }} />
                    {r.default_tz}
                  </span>
                </td>
                <td>
                  {r.enabled ? (
                    <span className="chip chip-pos" style={{ fontSize: 10 }}>
                      ● 启用
                    </span>
                  ) : (
                    <span className="chip" style={{ fontSize: 10, color: 'var(--fg-muted)' }}>
                      ○ 停用
                    </span>
                  )}
                </td>
                <td>
                  <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{r.updatedAt}</div>
                </td>
                <td>
                  {/* locked 时套一层 span 承接 tooltip — 共享 Switch 自身不接 title */}
                  <span
                    title={
                      r.lockEnabled
                        ? '英文为 fallback 边界 · DB CHECK 强制 enabled=true'
                        : undefined
                    }
                    style={{ display: 'inline-flex', verticalAlign: 'middle' }}
                  >
                    <Switch
                      key={`${r.language_code}:${switchResetNonce}`}
                      on={r.enabled}
                      locked={r.lockEnabled}
                      disabled={
                        !canWrite ||
                        toggleMutation.isPending ||
                        (r.enabled && (appsLoading || appsError))
                      }
                      onChange={(next) => toggle(r.language_code, next)}
                    />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {filtered.length === 0 && (
          <div style={{ padding: 28, textAlign: 'center', color: 'var(--fg-faint)', fontSize: 12 }}>
            无匹配语言
          </div>
        )}
      </div>

      {appsError && (
        <div className="chip chip-warn">
          引用 App 清单读取失败，停用语言前请重新读取。
          <button className="btn btn-sm btn-ghost" type="button" onClick={refetchApps}>
            重新读取
          </button>
        </div>
      )}
      {languageToDisable && (
        <ConfirmDialog
          title={`停用 ${languageToDisable.toUpperCase()}`}
          tone="warn"
          message="存量 App 仍下发该语言；新内容不再自动翻译，该语言退出运营配置 Tab，无法查看或手工维护。存量绑定不强制清理。"
          details={apps
            .filter((app) => app.lang.includes(languageToDisable))
            .map((app) => `${app.name} (${app.id})${app.status === 'paused' ? ' · 已停用' : ''}`)}
          confirmLabel="确认停用"
          onConfirm={() => toggleMutation.mutate({ code: languageToDisable, enabled: false })}
          onClose={closeLanguageWarning}
        />
      )}
      <ReadOnlyNote
        kind="language"
        items={[
          { code: 'system_languages.language_code', desc: 'ISO 639-1 · 主键 · 不可改' },
          {
            code: 'system_languages.app_selectable',
            desc: '是否可作 App 运行语言被选 · 只读 · 由 seed 维护',
          },
          {
            code: 'system_languages.default_timezone',
            desc: 'IANA 时区 · 书籍定时发布按此时区计算 · 只读 · 由 seed 维护',
          },
          {
            code: 'system_languages.display_name_i18n',
            desc: '多语言名 · 由 seed 维护，admin 不编辑',
          },
        ]}
        extra="EN 行不可停用 — DB CHECK 强制 enabled=true，作为请求语言越界时的 fallback 边界。ZH 行可启停但 app_selectable=false（中文不可作 App 运行语言被选）。"
      />
    </>
  );
}

/** 只读字段说明卡 — 列出由 seed / migration 维护、admin 不开放编辑的字段 */
function ReadOnlyNote({
  kind,
  items,
  extra,
}: {
  kind: 'region' | 'language';
  items: { code: string; desc: string }[];
  extra?: string;
}) {
  void kind;
  return (
    <div className="card-flat" style={{ padding: '12px 14px', background: 'var(--bg-subtle)' }}>
      <div className="hstack" style={{ gap: 8, marginBottom: 8 }}>
        <Icons.IconLock size={11} style={{ color: 'var(--fg-muted)' }} />
        <span style={{ fontSize: 11.5, fontWeight: 500, color: 'var(--fg)' }}>只读字段</span>
        <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          · 以下字段由发版 seed / DB migration 维护，admin 不开放编辑入口
        </span>
      </div>
      <div className="vstack" style={{ gap: 4 }}>
        {items.map((i) => (
          <div key={i.code} className="hstack" style={{ gap: 8 }}>
            <span
              className="mono"
              style={{ fontSize: 10.5, color: 'var(--fg-muted)', flexShrink: 0 }}
            >
              {i.code}
            </span>
            <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>· {i.desc}</span>
          </div>
        ))}
      </div>
      {extra && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px dashed var(--border)',
            fontSize: 11,
            color: 'var(--fg-muted)',
            lineHeight: 1.55,
          }}
        >
          {extra}
        </div>
      )}
    </div>
  );
}

/**
 * 运营弹窗 access level 表（ADM-SYS-S003 / A5 §11）——全系统一份、所有 App 共用。
 *
 * 与本页另外两张表不同的三点：① 写档 root/admin（不含 operator）；② 值是自由文本而非开关，
 * 故用「行内编辑 + 显式保存」而不是 Switch 乐观翻转；③ 清空是有意义的终态（该场景此后不下发），
 * 用独立的「清空」动作表达，避免与「保存空串」混淆。
 */
function PopupAccessLevelsTable() {
  const queryClient = useQueryClient();
  const canWrite = useCanWritePopupAccessLevels();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['popup-access-levels'],
    queryFn: () => systemConfigApi.listPopupAccessLevels(),
  });
  const rows: PopupAccessLevelResponse[] = data ?? [];
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // 按 scenario 记 pending，不用单个标量：多行并发保存时标量会互相覆盖 —— 后一次 setPending
  // 提前解锁前一行，任一请求完成时 setPending(null) 又把另一条仍在飞的行一并解锁，
  // 于是可重复 / 乱序写入，每次重复都白白递增一次全局纪元。
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());

  const draftOf = (r: PopupAccessLevelResponse) => drafts[r.scenario] ?? r.access_level_id ?? '';
  const dirty = (r: PopupAccessLevelResponse) => draftOf(r) !== (r.access_level_id ?? '');

  const save = async (scenario: PopupAccessLevelScenario, next: string | null) => {
    if (pending.has(scenario)) return; // 同一行重复提交直接丢弃
    setPending((p) => new Set(p).add(scenario));
    try {
      const updated = await systemConfigApi.setPopupAccessLevel(scenario, next);
      queryClient.setQueryData<PopupAccessLevelResponse[]>(['popup-access-levels'], (old) =>
        (old ?? []).map((r) => (r.scenario === updated.scenario ? updated : r)),
      );
      setDrafts((d) => {
        const { [scenario]: _drop, ...rest } = d;
        return rest;
      });
      toast(
        next === null
          ? `已清空「${scenarioLabel(scenario)}」· 该场景此后不再下发`
          : `已保存「${scenarioLabel(scenario)}」= ${next}`,
        { tone: 'pos', icon: 'IconCheck' },
      );
      await queryClient.invalidateQueries({ queryKey: ['popup-access-levels'] });
    } catch (err) {
      toast(err instanceof Error ? err.message : '保存失败', {
        tone: 'neg',
        icon: 'IconAlert',
      });
    } finally {
      setPending((p) => {
        const n = new Set(p);
        n.delete(scenario);
        return n;
      });
    }
  };

  const configured = rows.filter((r) => r.access_level_id).length;
  return (
    <>
      <div className="page-header" style={{ marginBottom: 0 }}>
        <div>
          <h2 style={{ font: 'inherit', fontSize: 16, fontWeight: 600, margin: 0 }}>
            运营弹窗权限档
          </h2>
          <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 3 }}>
            <span className="mono">system_popup_access_levels</span> · {rows.length} 个场景 ·{' '}
            <span style={{ color: 'var(--pos)' }}>{configured}</span> 已配置 · 全系统一份，所有 App
            共用
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0, marginTop: 12 }}>
        <Table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 200 }}>场景</th>
              <th>
                <span className="mono">access_level_id</span>
              </th>
              <th style={{ width: 170 }}>更新时间</th>
              <th style={{ width: 150 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.scenario}>
                <td>
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{scenarioLabel(r.scenario)}</div>
                  <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
                    {r.scenario}
                  </div>
                </td>
                <td>
                  <input
                    className="input mono"
                    value={draftOf(r)}
                    disabled={!canWrite || pending.has(r.scenario)}
                    placeholder="未配置 · 该场景不下发"
                    title={canWrite ? undefined : '权限不足 · 本值影响全部 App，限 root/admin'}
                    onChange={(e) => setDrafts((d) => ({ ...d, [r.scenario]: e.target.value }))}
                    style={{ maxWidth: 340 }}
                  />
                </td>
                <td style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  {new Date(r.updated_at).toLocaleString('zh-CN')}
                </td>
                <td>
                  <div className="hstack" style={{ gap: 6 }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-accent"
                      disabled={!canWrite || !dirty(r) || pending.has(r.scenario)}
                      title={
                        !canWrite
                          ? '权限不足 · 本值影响全部 App，限 root/admin'
                          : !dirty(r)
                            ? '没有改动'
                            : undefined
                      }
                      onClick={() =>
                        save(r.scenario, draftOf(r).trim() === '' ? null : draftOf(r).trim())
                      }
                    >
                      保存
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={!canWrite || r.access_level_id === null || pending.has(r.scenario)}
                      title={
                        r.access_level_id === null ? '该场景本就未配置' : '清空后该场景不再下发'
                      }
                      onClick={() => save(r.scenario, null)}
                    >
                      清空
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    padding: '26px 12px',
                    textAlign: 'center',
                    color: 'var(--fg-faint)',
                    fontSize: 11.5,
                  }}
                >
                  {isLoading
                    ? '加载中…'
                    : isError
                      ? '取数失败 · 刷新后重试'
                      : '场景行由 C 端 seed 预置，当前为空 —— 请联系后端确认库态'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <div
        className="card-flat"
        style={{ padding: '10px 12px', marginTop: 12, fontSize: 11.5, lineHeight: 1.6 }}
      >
        <span style={{ fontWeight: 500 }}>为什么不在运营弹窗页按 App 配</span>
        <span style={{ color: 'var(--fg-muted)' }}>
          ：同一场景在所有 App 下取同一个 <span className="mono">access_level_id</span>；App
          维度只决定该场景<b>是否上架</b>（运营管理 → 运营弹窗）。清空后该场景不再下发， 进行中的
          Stripe Checkout 由服务端按纪元走收敛路径。
        </span>
      </div>
    </>
  );
}
